import { pushEvent } from './dataLayer';

const CONSENT_KEY = 'myked_analytics_consent';
const CONSENT_TIMESTAMP_KEY = 'myked_analytics_consent_at';
const NOTICE_DISMISSED_KEY = 'myked_analytics_notice_dismissed';
const CONSENT_EXPIRY_DAYS = 365;
const FONT_AWESOME_KIT_ID = 'fa-kit';

export const CONSENT_GRANTED = 'granted';
export const CONSENT_DENIED = 'denied';

const consentListeners = new Set();

function isExpired(timestamp) {
  if (!timestamp) return true;
  const expiryMs = CONSENT_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
  return Date.now() - timestamp > expiryMs;
}

export function getStoredConsent() {
  try {
    const value = localStorage.getItem(CONSENT_KEY);
    const timestamp = Number(localStorage.getItem(CONSENT_TIMESTAMP_KEY));
    if (!value || isExpired(timestamp)) {
      return null;
    }
    return value;
  } catch (error) {
    return null;
  }
}

export function hasAnalyticsConsent() {
  // US opt-out: analytics on unless the visitor explicitly opted out.
  return getStoredConsent() !== CONSENT_DENIED;
}

function persistConsent(value) {
  try {
    localStorage.setItem(CONSENT_KEY, value);
    localStorage.setItem(CONSENT_TIMESTAMP_KEY, String(Date.now()));
  } catch (error) {
    // Ignore storage failures.
  }
}

function updateGtagConsent(granted) {
  if (typeof window.gtag !== 'function') return;

  const analyticsStatus = granted ? 'granted' : 'denied';
  window.gtag('consent', 'update', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: analyticsStatus,
  });
}

function notifyConsentListeners(choice) {
  consentListeners.forEach((listener) => {
    try {
      listener(choice);
    } catch (error) {
      // Ignore listener failures.
    }
  });
}

export function onConsentChange(listener) {
  consentListeners.add(listener);
  return () => consentListeners.delete(listener);
}

export function loadFontAwesome() {
  if (document.getElementById(FONT_AWESOME_KIT_ID)) return;

  const script = document.createElement('script');
  script.id = FONT_AWESOME_KIT_ID;
  script.src = 'https://kit.fontawesome.com/b87f9de083.js';
  script.crossOrigin = 'anonymous';
  document.head.appendChild(script);
}

export function grantAnalyticsConsent({ track = true } = {}) {
  persistConsent(CONSENT_GRANTED);
  updateGtagConsent(true);
  pushEvent({ event: 'consent_update', consent_status: CONSENT_GRANTED });
  notifyConsentListeners(CONSENT_GRANTED);

  if (track) {
    return CONSENT_GRANTED;
  }
  return CONSENT_GRANTED;
}

export function denyAnalyticsConsent({ track = true } = {}) {
  persistConsent(CONSENT_DENIED);
  updateGtagConsent(false);
  pushEvent({ event: 'consent_update', consent_status: CONSENT_DENIED });
  notifyConsentListeners(CONSENT_DENIED);

  if (track) {
    return CONSENT_DENIED;
  }
  return CONSENT_DENIED;
}

export function applyStoredConsent() {
  const stored = getStoredConsent();
  if (stored === CONSENT_DENIED) {
    denyAnalyticsConsent({ track: false });
    return CONSENT_DENIED;
  }

  grantAnalyticsConsent({ track: false });
  return CONSENT_GRANTED;
}

export function dismissAnalyticsNotice() {
  try {
    localStorage.setItem(NOTICE_DISMISSED_KEY, 'true');
  } catch (error) {
    // Ignore storage failures.
  }
}

export function shouldShowAnalyticsNotice() {
  if (getStoredConsent() === CONSENT_DENIED) {
    return false;
  }

  try {
    return localStorage.getItem(NOTICE_DISMISSED_KEY) !== 'true';
  } catch (error) {
    return true;
  }
}

/** @deprecated use shouldShowAnalyticsNotice */
export function shouldShowConsentBanner() {
  return shouldShowAnalyticsNotice();
}
